import React from 'react'
import { ArrowLeft2, CloseCircle } from 'iconsax-react'
import styled from 'styled-components'

import IconButton from 'components/lib/DesignSystem/V3/IconButton'
import Tooltip from 'components/lib/DesignSystem/V3/Tooltip'

import DrawerNav from '../../../DrawerNav'
import { SimulationStatusBadge } from '../../../SimulationSummary'

/**
 * Sticky drawer chrome, same anatomy as the redesigned incidents drawer header.
 *
 * Simulation view:
 *  Row 1: up/down nav between simulations + simulation name + status; close on the right.
 *  (The facts about the simulation live in the details card below, not in the chrome.)
 *
 * Email review (one delivered copy):
 *  Row 0: "Back to simulation details" text link, like the incidents page's back link,
 *         with close on the right so the X never moves.
 *  Row 1: up/down nav between this simulation's employees + employee name + email.
 *
 * Presentational: every action is a callback.
 */

const Wrap = styled.div`
  position: sticky;
  top: 0;
  z-index: 6;
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-2-5);
  padding: var(--t-spacing-3) var(--t-spacing-5) var(--t-spacing-2);
  background: var(--color-elevated-solid);
  box-shadow: 0 1px 0 var(--color-border);
  font-family: var(--t-typography-font-family-font-family-default);
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--t-spacing-3);
  min-width: 0;
`

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: var(--t-spacing-2);
  flex: 1 1 0;
  min-width: 0;
`

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: var(--t-spacing-1);
  flex-shrink: 0;
`

const Name = styled.span`
  font-size: var(--t-typography-font-size-lg);
  font-weight: var(--t-typography-font-weight-semi-bold);
  line-height: 1.3;
  color: var(--color-text);
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  word-break: break-word;
`

/* Same idiom as "Back to Incidents": a chevron and a label, no button chrome. */
const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--t-spacing-1-5);
  align-self: flex-start;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--t-typography-font-size-sm);
  font-weight: var(--t-typography-font-weight-medium);
  line-height: 1.3;

  &:hover,
  &:focus-visible {
    color: var(--color-text);
  }

  svg {
    flex-shrink: 0;
  }
`

const Email = styled.span`
  font-size: var(--t-typography-font-size-sm);
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const SimulationDrawerHeader = ({
  isPreview,
  simulation,
  participants,
  hasPrevSimulation,
  hasNextSimulation,
  onNavigateSimulation,
  activeParticipant,
  onNavigateParticipant,
  onBackToDetails,
  onClose,
}) => {
  const currentIndex = activeParticipant
    ? participants.findIndex(({ id }) => id === activeParticipant.id)
    : -1
  const hasPrevParticipant = currentIndex > 0
  const hasNextParticipant = currentIndex !== -1 && currentIndex < participants.length - 1

  const closeButton = (
    <Tooltip id="Psima_SimulationDrawer_Close_tooltip" content="Close">
      <IconButton
        id="Psima_SimulationDrawer_Close_button"
        ariaLabel="Close"
        dataTestId="Psima_SimulationDrawer_Close_button"
        size="small"
        variant="ghost"
        onClick={onClose}
      >
        <CloseCircle size={18} variant="Outline" />
      </IconButton>
    </Tooltip>
  )

  return (
    <Wrap data-testid="Psima_SimulationDrawer_Title_div">
      {isPreview && (
        <Row>
          <BackLink
            type="button"
            id="Psima_SimulationDrawer_BackToDetails_button"
            data-testid="Psima_SimulationDrawer_BackToDetails_button"
            onClick={onBackToDetails}
          >
            <ArrowLeft2 size={16} variant="Outline" />
            Back to simulation details
          </BackLink>
          <Right>{closeButton}</Right>
        </Row>
      )}
      <Row>
        <Left>
          {isPreview ? (
            <DrawerNav
              testIdPrefix="Psima_EmailPreview_ParticipantsNav"
              hasPrev={hasPrevParticipant}
              hasNext={hasNextParticipant}
              onNavigate={onNavigateParticipant}
              bare
              prevLabel="Previous employee"
              nextLabel="Next employee"
            />
          ) : (
            <DrawerNav
              testIdPrefix="Psima_SimulationDrawer_SimulationNav"
              hasPrev={hasPrevSimulation}
              hasNext={hasNextSimulation}
              onNavigate={onNavigateSimulation}
              bare
              prevLabel="Previous simulation"
              nextLabel="Next simulation"
            />
          )}
          {isPreview ? (
            <>
              <Name data-testid="Psima_SimulationDrawer_Name_span">{activeParticipant?.name}</Name>
              {activeParticipant?.email && (
                <Email
                  data-testid="Psima_SimulationDrawer_Email_span"
                  title={activeParticipant.email}
                >
                  {activeParticipant.email}
                </Email>
              )}
            </>
          ) : (
            <>
              <Tooltip
                id="Psima_SimulationDrawer_Name_tooltip"
                content={simulation?.title}
                disabled={!simulation?.title}
                onlyIfTruncated
              >
                <Name data-testid="Psima_SimulationDrawer_Name_span">{simulation?.title}</Name>
              </Tooltip>
              <SimulationStatusBadge simulation={simulation} />
            </>
          )}
        </Left>
        {!isPreview && <Right>{closeButton}</Right>}
      </Row>
    </Wrap>
  )
}

export default SimulationDrawerHeader
